/* eslint-disable no-nested-ternary */
import { ChannelType } from '@/features/_data/useUnitData';
import { StructureData } from '@/types/BaseSlide';
import { FrontendSlide } from '@/types/SlideType';
import { useUserInfo } from '@/features/_data/useUserInfo';
import { IconTrash } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { Sidebar } from '@/ui/Sidebar/Sidebar';
import { SwitchButton } from '@/ui/SwitchButton/SwitchButton';
import { T } from '@/ui/Text/Text';
import { Textarea } from '@/ui/Textarea/Textarea';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { createId } from '@/utils/createId';
import React, { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from 'react-select';
import Switch from 'react-switch';
import { ScannedTagEventData } from 'src/eventHandler/shared/renderSlide';
import { SensorType } from '@/types/SensorType';
import { MagicModel } from '@/types/MagicModel';
import { envIsElectron } from 'src/const/envCheck';
import { useLocalStorage } from 'usehooks-ts';
import { useParams } from 'react-router-dom';
import { useProductConfig } from '@/features/_data/useProductConfig';
import { ProductType } from '@/types/ProductType';
import { getTriggerOptions } from './TriggerOptions';
import {
  selectorStyle,
  StyleCheckbox,
  StyledButton,
  StyledControllersWrapper,
  StyledFormattedMessageCnt,
  StyledNFCMappingViewCnt,
  StyledSettingRow,
  StyledSettingTitle,
  StyledTagRow,
} from './styles';

export type TriggerOption = {
  readonly value: SensorType;
  readonly label: React.ReactNode;
  readonly inputType: string;
};
type Props = {
  readonly slideSplitScreenToggle: boolean;
  setSlideSplitScreenToggle: React.Dispatch<React.SetStateAction<boolean>>;
  readonly isOpen: boolean;
  readonly channelSelective: boolean;
  readonly languageSupport: boolean;
  readonly onClose: () => void;
  readonly slide: FrontendSlide;
  readonly onUpdateSlide: (slideData: Partial<FrontendSlide> | null) => void;
  readonly subtitle: string | undefined;
  readonly selectedType: SensorType | null;
  readonly magicModels: MagicModel[];
  readonly setSelectedType: React.Dispatch<
    React.SetStateAction<SensorType | null>
  >;
  isLoopVideoEnabled: boolean;
  onChangeIsLoopVideo: (isLoop: boolean) => void;
};

export type TriggerChannelOptions = {
  readonly value: string;
  readonly label: string;
  readonly inputType: string;
};

const normalizeTags = (
  tags: string[],
  channel: boolean,
  languageSupport: boolean,
) => {
  return tags.map((tag: string) => {
    const tagData = tag.split('|');
    const tagName = tagData[0];
    let tagChannel = '';
    let tagLanguage = '';

    if (channel && languageSupport) {
      // Both channel and language are enabled, expect format <tagId>|<channel>|<language>
      tagChannel = tagData[1] || '';
      tagLanguage = tagData[2] || '';
    } else if (channel) {
      // Only channel is enabled, expect format <tagId>|<channel>
      tagChannel = tagData[1] || '';
    } else if (languageSupport) {
      // Only language is enabled, expect format <tagId>|<language>
      tagLanguage = tagData[1] || '';
    }

    return { tagName, channel: tagChannel, language: tagLanguage };
  });
};

const convertTags = (
  triggerIds: (string | number)[],
  convertFunction: (input: string) => string | undefined,
) => {
  return triggerIds.map((tag) => {
    const tagString = String(tag); // Convert tag to string
    const [tagNameOrId, tagChannel, tagLanguage] = tagString.split('|');
    const convertedValue = convertFunction(tagNameOrId);
    const base = convertedValue || tagNameOrId;

    if (tagLanguage) {
      return `${base}|${tagChannel}|${tagLanguage}`;
    }
    if (tagChannel) {
      return `${base}|${tagChannel}`;
    }
    return base;
  });
};

export const SensorMappingSidebar = ({
  isOpen,
  channelSelective,
  languageSupport,
  slideSplitScreenToggle,
  setSlideSplitScreenToggle,
  onClose,
  slide,
  onUpdateSlide,
  subtitle,
  selectedType,
  magicModels,
  setSelectedType,
  isLoopVideoEnabled,
  onChangeIsLoopVideo,
}: Props) => {
  const toast = useToast();
  const intl = useIntl();
  const { isAdmin } = useUserInfo();
  const [nfcExpertMode, setNfcExpertMode] = useLocalStorage(
    'nfcExpertMode',
    false,
  );

  const [selectedSplitScreenOption, setSelectedSplitScreenOption] =
    useState<null | { label: string; value: string }>(null);
  const [simpleViewTags, setSimpleViewTags] = useState<
    {
      tagName: string;
      channel: string;
      language: string;
    }[]
  >([]);

  const [prevTextareaValue, setPrevTextareaValue] = useState<string>('');

  const [form, setForm] = useState({
    [SensorType.HOME]: '',
    [SensorType.NEXMO_NFC]: '',
    [SensorType.NEXMO_PICKUP]: '',
    [SensorType.GENERIC]: '',
    [SensorType.TH_PUSH]: '',
    [SensorType.NEXMO_BUTTON]: '',
    [SensorType.NEXMO_PRESENCE_XY240]: '',
    [SensorType.UHF_NEO_DESKTOP]: '',
    [SensorType.TOUCH]: '',
  });

  const { id: unitId } = useParams();

  const { productType } = useProductConfig(unitId);

  const slideProps: Omit<StructureData, 'triggerIds'> = {
    id: slide.structureData?.id ?? createId(),
    children: [],
    initialState: slide.structureData?.initialState ?? null,
    sensorIds: slide.structureData?.sensorIds ?? [],
    slideGroupId: slide.slideGroupId ?? '',
    slideIds: slide.slideId ? [slide.slideId] : [],
  };

  const getMagicModelByTagId = (tagId: string) => {
    return magicModels.find((MM) => MM.sensorId === tagId);
  };

  const checkIfTagAreAtSensorLibrary = (addedTagNames: string[]) => {
    const isEveryTagAtSensorLibrary = addedTagNames.every((addedTag) => {
      const addedTagsWithoutChannels = addedTag.split('|')[0];
      return magicModels.find(
        (MM) => MM.modelName === addedTagsWithoutChannels,
      );
    });

    return isEveryTagAtSensorLibrary;
  };

  const getSplitScreenOption = () => {
    return slide.structureData?.triggerIds.find(
      (trigger) => trigger.type === SensorType.SPLITSCREEN,
    );
  };

  const handleSidebarClose = async () => {
    if (!magicModels) {
      return onClose();
    }

    const addedTags = [
      ...new Set(
        form[SensorType.NEXMO_NFC]
          ?.split('\n')
          .map((tag) => tag.trim().split('|')[0])
          .filter((tag) => tag),
      ),
    ];

    if (form[SensorType.NEXMO_NFC] && magicModels.length) {
      if (!isAdmin && !checkIfTagAreAtSensorLibrary(addedTags)) {
        if (selectedType === 'NEXMO_NFC') {
          toast({
            title: intl.formatMessage({
              defaultMessage: 'Invalid tag mapped!',
              id: 'LMt3CL',
            }),
            subtitle: intl.formatMessage({
              defaultMessage:
                'Map the tag to a name in the SensorLibrary first.',
              id: 'dmyL48',
            }),
            mode: TOASTTYPE.Danger,
          });
        }
      }
    }

    return onClose();
  };

  const mockedAvailableTags = ['040F86EA6A5B80', '04DD0BEA6A5B84'];
  const [mockedPlacedTags, setMockedPlacedTags] = useState<string[]>([]);

  const options: readonly TriggerOption[] = getTriggerOptions(
    isAdmin,
    productType === ProductType.TrihowPad,
  );

  const channelOptions: readonly TriggerChannelOptions[] = [
    {
      value: ChannelType.CHANNEL_1,
      label: '1',
      inputType: 'checkbox',
    },
    {
      value: ChannelType.CHANNEL_2,
      label: '2',
      inputType: 'checkbox',
    },
    {
      value: ChannelType.CHANNEL_3,
      label: '3',
      inputType: 'checkbox',
    },
    {
      value: ChannelType.CHANNEL_4,
      label: '4',
      inputType: 'checkbox',
    },
  ];

  const languageOptions: readonly TriggerChannelOptions[] = [
    {
      value: 'DE',
      label: 'DE',
      inputType: 'checkbox',
    },
    {
      value: 'IT',
      label: 'IT',
      inputType: 'checkbox',
    },
    {
      value: 'FR',
      label: 'FR',
      inputType: 'checkbox',
    },
    {
      value: 'EN',
      label: 'EN',
      inputType: 'checkbox',
    },
    {
      value: 'JPN',
      label: 'JPN',
      inputType: 'checkbox',
    },
    {
      value: 'KOR',
      label: 'KOR',
      inputType: 'checkbox',
    },
    {
      value: 'CHN',
      label: 'CHN',
      inputType: 'checkbox',
    },
  ];

  const findMMNameByTagId = (tagId: string) => {
    return magicModels.find((MM) => MM.sensorId === tagId)?.modelName;
  };

  const findTagIdByName = (tagName: string) => {
    return magicModels.find((MM) => MM.modelName === tagName)?.sensorId;
  };

  const convertMMNamesToTagIds = (triggerIds: string[]) => {
    return convertTags(triggerIds, findTagIdByName);
  };

  const convertTagIdsToMMNames = (triggerIds: string[]) => {
    return convertTags(triggerIds, findMMNameByTagId);
  };

  useEffect(() => {
    if (nfcExpertMode || !isOpen) return;

    const foundTriggeredData = slide.structureData?.triggerIds.find((data) => {
      return data.type === 'NEXMO_NFC';
    });

    if (foundTriggeredData?.values) {
      const tags = convertTagIdsToMMNames(foundTriggeredData?.values);

      const normalizedTags = normalizeTags(
        tags,
        channelSelective,
        languageSupport,
      );
      setSimpleViewTags(normalizedTags);
    } else setSimpleViewTags([]);
  }, [nfcExpertMode, isOpen, slide.structureData?.triggerIds]);

  const deleteSplitScreenOption = () => {
    if (!slide.structureData) return;
    const updatedTriggerIds = slide.structureData.triggerIds.filter(
      (trigger) => trigger.type !== SensorType.SPLITSCREEN,
    );

    onUpdateSlide({
      structureData: {
        ...slideProps,
        triggerIds: updatedTriggerIds,
      },
    });
  };

  const addSplitScreenOption = () => {
    const selectedValue = findTagIdByName(
      selectedSplitScreenOption?.value ?? '',
    );
    let updatedTriggerIds = slide.structureData
      ? slide.structureData.triggerIds
      : [];
    const isSplitScreenOptionAlreadyAdded = getSplitScreenOption();

    if (isSplitScreenOptionAlreadyAdded) {
      if (isSplitScreenOptionAlreadyAdded.values[0] === selectedValue) return;

      updatedTriggerIds = updatedTriggerIds.map((trigger) => {
        if (trigger.type === SensorType.SPLITSCREEN) {
          return {
            type: SensorType.SPLITSCREEN,
            values: [selectedValue],
          };
        }
        return trigger;
      });
    } else {
      updatedTriggerIds = [
        ...updatedTriggerIds,
        {
          type: SensorType.SPLITSCREEN,
          values: [selectedValue],
        },
      ];
    }

    onUpdateSlide({
      structureData: {
        ...slideProps,
        triggerIds: [...updatedTriggerIds],
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      const isSplitScreenOptionAlreadyAdded = getSplitScreenOption()?.values[0];

      const splitScreenValue =
        findMMNameByTagId(getSplitScreenOption()?.values[0]) ||
        magicModels[0]?.modelName;

      if (isSplitScreenOptionAlreadyAdded) {
        setSlideSplitScreenToggle(true);

        setSelectedSplitScreenOption({
          label: splitScreenValue,
          value: splitScreenValue,
        });
      }

      if (selectedType === 'NEXMO_NFC') {
        const foundTriggeredData = slide.structureData?.triggerIds.find(
          (data) => {
            return data.type === 'NEXMO_NFC';
          },
        );

        if (foundTriggeredData) {
          const convertedTriggeredData = convertTagIdsToMMNames(
            foundTriggeredData.values,
          );
          setForm((data) => {
            return {
              ...data,
              [selectedType as keyof typeof form]:
                prevTextareaValue || convertedTriggeredData.join('\n'),
            };
          });
        }
      }
    } else if (!isOpen && selectedType === 'NEXMO_NFC') {
      const addedTags = [
        ...new Set(
          form[SensorType.NEXMO_NFC]
            ?.split('\n')
            .map((tag) => tag.trim())
            .filter((tag) => tag),
        ),
      ];

      const isEveryTagAtWhitelist = checkIfTagAreAtSensorLibrary(addedTags);

      const shouldAddNewTags =
        isEveryTagAtWhitelist || !magicModels.length || isAdmin;

      if (shouldAddNewTags) {
        setPrevTextareaValue(form[SensorType.NEXMO_NFC]);
      } else {
        setForm((data) => {
          return {
            ...data,
            [selectedType as keyof typeof form]: prevTextareaValue,
          };
        });

        onUpdateSlide({
          structureData: {
            ...slideProps,
            triggerIds: options
              .map((sensorOption) => {
                return {
                  type: sensorOption.value,
                  values:
                    // eslint-disable-next-line no-nested-ternary
                    selectedType === sensorOption.value
                      ? prevTextareaValue
                        ? prevTextareaValue.split('\n')
                        : form[SensorType.NEXMO_NFC].split('\n')
                      : (slide.structureData?.triggerIds.find(
                          (trigger) => trigger.type === sensorOption.value,
                        )?.values ?? []),
                };
              })
              .filter((sensorOption) => Boolean(sensorOption.values?.length)),
          },
        });
      }
    }
  }, [isOpen, selectedType]);

  useEffect(() => {
    if (slideSplitScreenToggle && !selectedSplitScreenOption) {
      setSelectedSplitScreenOption({
        label: magicModels[0]?.modelName,
        value: magicModels[0]?.modelName,
      });
    } else if (slideSplitScreenToggle && selectedSplitScreenOption) {
      setSelectedSplitScreenOption(selectedSplitScreenOption);

      addSplitScreenOption();
    }
  }, [slideSplitScreenToggle]);

  useEffect(() => {
    if (!selectedSplitScreenOption) return;
    addSplitScreenOption();
  }, [selectedSplitScreenOption]);

  const setNewIPCTag = (
    value: string,
    option: TriggerOption,
    isScanned?: boolean,
  ) => {
    let newValue: null | string | string[] = null;

    switch (option.inputType) {
      case 'input':
        newValue = [value];
        break;
      case 'textfield':
        // eslint-disable-next-line no-case-declarations
        const prevValues =
          slide.structureData?.triggerIds
            .find((trigger) => trigger.type === option.value)
            ?.values.join('\n') || null;
        if (isScanned) {
          if (prevValues) {
            const newValueData = value.split('|');
            const matchedId = findTagIdByName(newValueData[0]);
            const prevValuesArray = prevValues.split('\n');
            const isTagAlreadyAdded = newValueData[1]
              ? prevValues
                  .split('\n')
                  .includes(`${matchedId}|${newValueData[1]}`)
              : prevValuesArray.includes(matchedId as string);

            const convertedPreviusValues =
              convertTagIdsToMMNames(prevValuesArray);

            newValue = isTagAlreadyAdded
              ? convertedPreviusValues
              : `${convertedPreviusValues.join('\n')}\n${value}`.split('\n');
          } else {
            newValue = value.split('\n');
          }

          setForm((data) => {
            return {
              ...data,
              [selectedType as keyof typeof form]: (newValue as string[]).join(
                '\n',
              ),
            };
          });
        } else {
          newValue = value.split('\n');
        }

        break;
      case 'textfieldJSON':
        newValue = value.split('\n');
        break;
      default:
        newValue = value;
    }

    const convertedToTagIds = convertMMNamesToTagIds(
      Array.isArray(newValue) ? newValue : [newValue],
    );

    if (convertedToTagIds.length === 1 && convertedToTagIds[0] === '')
      convertedToTagIds.pop();

    onUpdateSlide({
      structureData: {
        ...slideProps,
        triggerIds: options
          .map((sensorOption) => {
            return {
              type: sensorOption.value,
              values:
                // eslint-disable-next-line no-nested-ternary
                option.value === sensorOption.value
                  ? value
                    ? convertedToTagIds
                    : []
                  : (slide.structureData?.triggerIds.find(
                      (trigger) => trigger.type === sensorOption.value,
                    )?.values ?? []),
            };
          })
          .filter((sensorOption) => Boolean(sensorOption.values?.length)),
      },
    });
  };

  useEffect(() => {
    if (process.env.MOCKED_READER === 'false' || !envIsElectron) return;
    if (isOpen && mockedPlacedTags.length > 0) {
      const interval = setTimeout(() => {
        window.electron.sendMockedReaderScanResponse(mockedPlacedTags);
      }, 200);
      // eslint-disable-next-line consistent-return
      return () => clearInterval(interval);
    }
  }, [mockedPlacedTags, isOpen]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isOpen) {
      const handleScanIpcTag = (tagData: ScannedTagEventData) => {
        const detectedMagicModel = getMagicModelByTagId(tagData.value.tagId);
        if (!detectedMagicModel) {
          return toast({
            title: intl.formatMessage({
              defaultMessage: 'Tag does not match a MagicModel!',
              id: 'wJx/1W',
            }),
            mode: TOASTTYPE.Danger,
          });
        }

        setForm((data) => {
          return {
            ...data,
            [selectedType as keyof typeof form]: channelSelective
              ? `${detectedMagicModel.modelName}|${tagData.value.channel}`
              : `${detectedMagicModel.modelName}`,
          };
        });

        const option = options.find(
          (optionData) => optionData.value === selectedType,
        );

        setNewIPCTag(
          channelSelective
            ? `${detectedMagicModel.modelName}|${tagData.value.channel}`
            : `${detectedMagicModel.modelName}`,
          option as TriggerOption,
          true,
        );
      };

      let eventToRemove: (() => void) | undefined;

      if (envIsElectron) {
        eventToRemove = window.electron.ipcRenderer.on(
          'emitTagId',
          (tagData: any) => handleScanIpcTag(tagData as ScannedTagEventData),
        );
      } else {
        console.warn('IPC functionality is only available in Electron');
      }
      return () => {
        if (eventToRemove) {
          try {
            eventToRemove();
          } catch (err) {
            console.error('Error removing event listener:', err);
          }
        }
      };
    }
  }, [selectedType, slide, isOpen]);

  const addNewTagFromSimpleView = (
    value: string,
    currentValues: string[],
    option: TriggerOption,
  ) => {
    const newTagName = channelSelective
      ? `${value}|${channelOptions[0].value}`
      : value;

    if (!value) return;

    const isTagAllowed = isAdmin || checkIfTagAreAtSensorLibrary([value]);

    if (!isTagAllowed) {
      return toast({
        title: intl.formatMessage({
          defaultMessage: 'User not allowed to add Tag.',
          id: '2Jz8mK',
        }),
        mode: TOASTTYPE.Danger,
      });
    }

    const upatedNFCTagList = [...currentValues, newTagName].join('\n');

    setForm((data) => {
      return {
        ...data,
        [option.value as keyof typeof form]: upatedNFCTagList,
      };
    });

    setNewIPCTag(upatedNFCTagList, option);
  };

  const updateTagFromSimpleView = (
    index: number,
    tagName: string,
    currentValues: string[],
    option: TriggerOption,
  ) => {
    const upatedNFCTagList = currentValues
      .map((tag, tagIndex) => {
        if (tagIndex === index) {
          const tagData = tag.split('|');

          return channelSelective ? `${tagName}|${tagData[1]}` : tagName;
        }
        return tag;
      })
      .join('\n');

    setForm((data) => {
      return {
        ...data,
        [option.value as keyof typeof form]: upatedNFCTagList,
      };
    });
    setNewIPCTag(upatedNFCTagList, option);
  };

  const deleteTagFromSimpleView = (
    index: number,
    currentValues: string[],
    option: TriggerOption,
  ) => {
    const upatedNFCTagList = currentValues
      .filter((_, tagIndex) => tagIndex !== index)
      .join('\n');

    if (!upatedNFCTagList.length) setSimpleViewTags([]);

    setForm((data) => {
      return {
        ...data,
        [option.value as keyof typeof form]: upatedNFCTagList,
      };
    });

    setNewIPCTag(upatedNFCTagList, option);
  };

  const changeTagChannel = (
    index: number,
    tagChannel: string,
    currentValues: string[],
    option: TriggerOption,
  ) => {
    const upatedNFCTagList = currentValues
      .map((tag, tagIndex) => {
        if (tagIndex === index) {
          const tagData = tag.split('|');

          return `${tagData[0]}|${tagChannel}`;
        }

        return tag;
      })
      .join('\n');

    setForm((data) => {
      return {
        ...data,
        [option.value as keyof typeof form]: upatedNFCTagList,
      };
    });
    setNewIPCTag(upatedNFCTagList, option);
  };

  const changeTagLanguage = (
    index: number,
    language: string,
    currentValues: string[],
    option: TriggerOption,
  ) => {
    const updatedNFCTagList = currentValues
      .map((tag, tagIndex) => {
        if (tagIndex === index) {
          const tagData = tag.split('|');
          const tagName = tagData[0];
          const tagChannel = tagData[1];

          // Return the updated tag based on the presence of the channel
          return channelSelective
            ? `${tagName}|${tagChannel}|${language}`
            : `${tagName}|${language}`;
        }

        // Return the unchanged tag if it's not the one being updated
        return tag;
      })
      .join('\n');

    // Update the form state with the new tag list
    setForm((data) => ({
      ...data,
      [option.value as keyof typeof form]: updatedNFCTagList,
    }));

    setNewIPCTag(updatedNFCTagList, option);
  };

  const splitScreenErrorLookup = {
    'no-models': intl.formatMessage({
      defaultMessage: 'Split Screen Unavailable - No Magic Models available',
      id: '/O9o9n',
    }),
    'is-web-slide': intl.formatMessage({
      defaultMessage:
        'Split Screen Unavailable - Webslides can not be split yet',
      id: 'k7pE5y',
    }),
  };

  const splitScreenError =
    slide.contentType === 'text/html'
      ? splitScreenErrorLookup['is-web-slide']
      : splitScreenErrorLookup['no-models'];

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={handleSidebarClose}
      title={
        <FormattedMessage
          defaultMessage="Slide {slideName}"
          values={{ slideName: subtitle }}
          id="NLw70v"
        />
      }
      scrollable
      testId="sensor-mapping-sidebar-wrapper"
    >
      <Flex direction="column" gap={12} grow>
        {slide.contentType === 'video/mp4' ? (
          <Flex gap={12} direction="column">
            <T h3>
              <FormattedMessage defaultMessage="Settings" id="D3idYv" />
            </T>
            <T p>
              <FormattedMessage
                defaultMessage="Should the Video play in an endless loop or not"
                id="bYtRSc"
              />
            </T>

            <SwitchButton
              onSelectionChange={(newValue) => {
                onChangeIsLoopVideo(newValue === 'loop');
              }}
              selectedButton={isLoopVideoEnabled ? 'loop' : 'play-once'}
              buttons={[
                {
                  id: 'loop',
                  label: (
                    <FormattedMessage defaultMessage="Loop video" id="ypRmcp" />
                  ),
                },
                {
                  id: 'play-once',
                  label: (
                    <FormattedMessage
                      defaultMessage="Play video once"
                      id="NLD6rM"
                    />
                  ),
                },
              ]}
            />
          </Flex>
        ) : null}
        {slide.contentType !== 'text/html' && magicModels.length ? (
          <>
            <Flex gap={12} direction="column">
              <T p>
                <FormattedMessage
                  defaultMessage="Define this slide as a Split Screen Slide"
                  id="Zx2ONE"
                />
              </T>

              <StyledControllersWrapper>
                <StyledSettingRow enabled={Boolean(slideSplitScreenToggle)}>
                  <Switch
                    className="switch"
                    onChange={() => {
                      setSlideSplitScreenToggle((toggle) => {
                        if (toggle) {
                          deleteSplitScreenOption();
                        }

                        return !toggle;
                      });
                    }}
                    checked={Boolean(slideSplitScreenToggle)}
                  />
                  <Select
                    className="select"
                    isDisabled={!slideSplitScreenToggle}
                    isSearchable={false}
                    onChange={(newValue) => {
                      setSelectedSplitScreenOption(newValue);
                    }}
                    options={magicModels.map((model) => {
                      return {
                        value: model.modelName,
                        label: model.modelName,
                      };
                    })}
                    placeholder="Magic Model"
                    styles={selectorStyle}
                    value={
                      selectedSplitScreenOption || {
                        value: magicModels[0]?.modelName,
                        label: magicModels[0]?.modelName,
                      }
                    }
                  />
                </StyledSettingRow>
              </StyledControllersWrapper>

              <div style={{ marginBottom: 16 }} />
            </Flex>
          </>
        ) : (
          <StyledFormattedMessageCnt p>
            {splitScreenError}
          </StyledFormattedMessageCnt>
        )}

        <T h3>
          <FormattedMessage defaultMessage="Sensor Mapping" id="T1/uey" />
        </T>
        <Select
          isSearchable={false}
          onChange={(newValue) => {
            if (!newValue) return;
            setSelectedType(newValue.value);
          }}
          options={options.filter(
            (option) => option.value !== SensorType.SPLITSCREEN,
          )}
          placeholder="Type"
          styles={selectorStyle}
          value={
            options
              .filter((option) => option.value !== SensorType.SPLITSCREEN)
              .find(({ value }) => selectedType === value) ?? options[0]
          }
        />
        <T h4>
          {
            {
              input: <FormattedMessage defaultMessage="Add ID" id="1K6BFO" />,
              textfield: (
                <FormattedMessage defaultMessage="Add IDs" id="4cFabT" />
              ),
              textfieldJSON:
                selectedType === SensorType.GENERIC ? (
                  <FormattedMessage
                    defaultMessage="Add text or JSON"
                    id="um1rRc"
                  />
                ) : (
                  <FormattedMessage defaultMessage="Add JSON" id="q5DLJS" />
                ),
            }[
              options.find(({ value }) => value === selectedType)?.inputType ??
                'input'
            ]
          }
        </T>
        {options
          .filter((option) => option.value === selectedType)
          .map((option) => {
            const propsForSlide: Omit<StructureData, 'triggerIds'> = {
              id: slide.structureData?.id ?? createId(),
              children: [],
              initialState: slide.structureData?.initialState ?? null,
              sensorIds: slide.structureData?.sensorIds ?? [],
              slideGroupId: slide.slideGroupId ?? '',
              slideIds: slide.slideId ? [slide.slideId] : [],
            };

            const currentTriggerValues =
              slide.structureData?.triggerIds.find(
                (trigger) => trigger.type === option.value,
              )?.values || [];

            const convertedCurrentTriggerValues =
              convertTagIdsToMMNames(currentTriggerValues);

            switch (option.inputType) {
              case 'input':
                return (
                  <Fragment key={option.value}>
                    {option.value === SensorType.TH_PUSH &&
                    process.env.NODE_ENV === 'development' ? (
                      <StyledControllersWrapper>
                        {mockedAvailableTags.map((tag, index) => (
                          <Button
                            mode={
                              mockedPlacedTags.includes(tag)
                                ? 'primary'
                                : 'secondary'
                            }
                            key={`${tag}-${index.toLocaleString()}`}
                            onClick={() => {
                              setMockedPlacedTags((prev) => {
                                return prev.includes(tag) ? [] : [tag];
                              });
                            }}
                          >
                            {tag}
                          </Button>
                        ))}
                      </StyledControllersWrapper>
                    ) : null}
                    <Input
                      name={form[option.value as keyof typeof form]}
                      onChange={(value) => {
                        setForm((data) => {
                          return {
                            ...data,
                            [option.value as keyof typeof form]: value,
                          };
                        });
                        setNewIPCTag(value, option);
                      }}
                      value={
                        (form[option.value as keyof typeof form] ||
                          slide?.structureData?.triggerIds
                            .find((trigger) => trigger.type === option.value)
                            ?.values.join('\n')) ??
                        ''
                      }
                    />
                  </Fragment>
                );
              case 'textfield':
                return (
                  <Fragment key={option.value}>
                    {option.value === SensorType.NEXMO_NFC &&
                    ['development', 'test'].includes(
                      process.env.NODE_ENV ?? '',
                    ) ? (
                      <StyledControllersWrapper data-testid="sensor-mapping-sidebar-textfield">
                        {mockedAvailableTags.map((tag, index) => (
                          <Button
                            mode={
                              mockedPlacedTags.includes(tag)
                                ? 'primary'
                                : 'secondary'
                            }
                            key={`${tag}-${index.toLocaleString()}`}
                            onClick={() => {
                              setMockedPlacedTags((prev) => {
                                return prev.includes(tag) ? [] : [tag];
                              });
                            }}
                          >
                            {tag}
                          </Button>
                        ))}
                      </StyledControllersWrapper>
                    ) : null}
                    {isAdmin && option.value === SensorType.NEXMO_NFC && (
                      <StyledSettingRow enabled={Boolean(nfcExpertMode)}>
                        <StyledSettingTitle>Expert Mode:</StyledSettingTitle>
                        <Switch
                          className="switch"
                          onChange={(checked: boolean) =>
                            setNfcExpertMode(checked)
                          }
                          checked={Boolean(nfcExpertMode)}
                        />
                      </StyledSettingRow>
                    )}

                    {nfcExpertMode ? (
                      <Textarea
                        name={form[option.value as keyof typeof form]}
                        key={option.value}
                        onChange={(event) => {
                          setForm((data) => {
                            return {
                              ...data,
                              [option.value as keyof typeof form]:
                                event.target.value,
                            };
                          });
                          setNewIPCTag(event.target.value, option);
                        }}
                        regex={/[a-zA-Z0-9\n|]/}
                        value={
                          (form[option.value as keyof typeof form] ||
                            slide?.structureData?.triggerIds
                              .find((trigger) => trigger.type === option.value)
                              ?.values.join('\n')) ??
                          ''
                        }
                      />
                    ) : (
                      <StyledNFCMappingViewCnt direction="column">
                        {simpleViewTags.map((tagData, index) => {
                          const {
                            tagName,
                            channel: tagChannel,
                            language,
                          } = tagData;

                          return (
                            <StyledTagRow
                              key={`${tagName}-${index.toLocaleString()}`}
                            >
                              <Select
                                isSearchable={false}
                                options={magicModels.map((model) => ({
                                  value: model.modelName,
                                  label: model.modelName,
                                }))}
                                placeholder="Magic Model Name"
                                styles={selectorStyle}
                                onChange={(newValue) => {
                                  updateTagFromSimpleView(
                                    index,
                                    newValue?.value ?? '',
                                    convertedCurrentTriggerValues,
                                    option,
                                  );
                                }}
                                className="tag-id"
                                value={{ label: tagName, value: tagName }}
                              />
                              {tagChannel && (
                                <Select
                                  isSearchable={false}
                                  onChange={(newValue) => {
                                    if (newValue?.value) {
                                      changeTagChannel(
                                        index,
                                        newValue.value,
                                        convertedCurrentTriggerValues as string[],
                                        option,
                                      );
                                    }
                                  }}
                                  options={channelOptions}
                                  placeholder="Channel"
                                  styles={selectorStyle}
                                  value={{
                                    value: tagChannel,
                                    label: tagChannel,
                                  }}
                                />
                              )}
                              {languageSupport && (
                                <Select
                                  isSearchable={false}
                                  onChange={(newValue) => {
                                    if (newValue?.value) {
                                      changeTagLanguage(
                                        index,
                                        newValue.value,
                                        convertedCurrentTriggerValues,
                                        option,
                                      );
                                    }
                                  }}
                                  options={languageOptions}
                                  placeholder="Language"
                                  styles={selectorStyle}
                                  value={{
                                    value: language,
                                    label: language,
                                  }}
                                />
                              )}
                              <Button
                                mode="transparent"
                                onClick={() => {
                                  deleteTagFromSimpleView(
                                    index,
                                    convertedCurrentTriggerValues,
                                    option,
                                  );
                                }}
                              >
                                <T link>
                                  <IconTrash />
                                </T>
                              </Button>
                            </StyledTagRow>
                          );
                        }) || null}
                        {!nfcExpertMode && (
                          <StyledTagRow>
                            <Select
                              isSearchable={false}
                              options={magicModels.map((model) => {
                                return {
                                  value: model.modelName,
                                  label: model.modelName,
                                };
                              })}
                              placeholder="Magic Model Name"
                              styles={selectorStyle}
                              className="tag-id"
                              onChange={(e) =>
                                addNewTagFromSimpleView(
                                  e?.value ?? '',
                                  convertedCurrentTriggerValues,
                                  option,
                                )
                              }
                              value={null}
                            />
                          </StyledTagRow>
                        )}
                      </StyledNFCMappingViewCnt>
                    )}
                  </Fragment>
                );
              case 'textfieldJSON': {
                const inputValue =
                  slide?.structureData?.triggerIds
                    .find((trigger) => trigger.type === option.value)
                    ?.values.map((value) =>
                      typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : value,
                    )
                    .join('') ?? '';
                const hasError = (() => {
                  if (!inputValue) {
                    return false;
                  }
                  if (option.value === SensorType.GENERIC) {
                    return false;
                  }
                  try {
                    const parsedValue = JSON.parse(inputValue);
                    if (typeof parsedValue === 'object') {
                      return false;
                    }
                    return true;
                  } catch (error) {
                    return true;
                  }
                })();

                return (
                  <Textarea
                    name={form[option.value as keyof typeof form]}
                    hasError={hasError}
                    key={option.value}
                    onChange={(event) => {
                      setForm((data) => {
                        return {
                          ...data,
                          [option.value as keyof typeof form]:
                            event.target.value,
                        };
                      });
                      setNewIPCTag(event.target.value, option);
                    }}
                    value={
                      (form[option.value as keyof typeof form] ||
                        slide?.structureData?.triggerIds
                          .find((trigger) => trigger.type === option.value)
                          ?.values.join('\n')) ??
                      ''
                    }
                  />
                );
              }
              case 'checkbox':
                return (
                  <StyleCheckbox key={option.value}>
                    <FormattedMessage
                      defaultMessage="Enable Slide?"
                      id="K3e9Ks"
                    />
                    <input
                      name={form[option.value as keyof typeof form]}
                      checked={
                        Boolean(form[option.value as keyof typeof form]) ||
                        Boolean(
                          (slide?.structureData?.triggerIds
                            .find((trigger) => trigger.type === option.value)
                            ?.values.join('') ?? '') === option.value,
                        )
                      }
                      key={option.value}
                      onChange={({ target: { checked } }) => {
                        const { value } = option;
                        onUpdateSlide({
                          structureData: {
                            ...propsForSlide,
                            triggerIds: options
                              .map((sensorOption) => {
                                return {
                                  type: sensorOption.value,
                                  values:
                                    option.value === sensorOption.value
                                      ? value
                                        ? [value]
                                        : []
                                      : (slide.structureData?.triggerIds.find(
                                          (trigger) =>
                                            trigger.type === sensorOption.value,
                                        )?.values ?? []),
                                };
                              })
                              .filter((sensorOption) =>
                                Boolean(sensorOption.values?.length),
                              )
                              .filter((trigger) => {
                                if (trigger.type === 'HOME')
                                  return trigger?.type === value && checked;
                                return true;
                              }),
                          },
                        });
                        if (slide.contentType === 'video/mp4') {
                          onChangeIsLoopVideo(true);
                        }
                      }}
                      type="checkbox"
                    />
                  </StyleCheckbox>
                );
              default:
                return null;
            }
          })}
      </Flex>
      <Flex align="end">
        <StyledButton onClick={handleSidebarClose} size="large">
          <FormattedMessage defaultMessage="Close" id="rbrahO" />
        </StyledButton>
      </Flex>
    </Sidebar>
  );
};
